export const routes = {
  403: '/403',
  404: '/404',
  500: '/500',
  auth: {
    signIn: (props?: { returnPath?: string; errorMessage?: string }) => {
      let query = '';

      if (props?.returnPath) query += `?returnUrl=${encodeURIComponent(props?.returnPath)}`;
      if (props?.errorMessage)
        query += query
          ? `&error=${encodeURIComponent(props?.errorMessage)}`
          : `?error=${encodeURIComponent(props?.errorMessage)}`;
      return `/auth/signin${query}`;
    },
    auth0: {
      login: (returnUrl?: string) => (returnUrl ? `/api/auth/login?returnUrl=${returnUrl}` : '/api/auth/login'),
      logout: '/api/auth/logout',
    },
  },
  dashboard: '/',
  nodeSwitch: '/clients-node-switch',
  firmNodes: { root: '/firm/nodes', node: (nodeId: string) => `/firm/nodes/${nodeId}` },
  clients: {
    root: '/clients',
    create: '/clients/create',
    groups: (id?: string) => ({
      root: `/clients/groups${id ? `?portfolioGroups_f.id=${encodeURIComponent(id)}` : ''}`,
      create: '/clients/groups/create',
      group: (id?: string) => ({
        root: `/clients/groups/${id}/overview`,
        details: `/clients/groups/${id}/details`,
        edit: `/clients/groups/${id}/edit`,
        nominate: `/clients/groups/${id}/nominate`,
      }),
    }),
    corporate: {
      create: `/clients/corporate/create`,
      client: (id: string) => ({
        edit: (stepId?: string) => `/clients/corporate/${id}/edit${stepId ? `#${stepId}` : ''}`,
      }),
    },
    trust: {
      create: `/clients/trust/create`,
      client: (id: string) => ({
        edit: (stepId?: string) => `/clients/trust/${id}/edit${stepId ? `#${stepId}` : ''}`,
      }),
    },
    child: {
      create: '/clients/child/create',
    },
    client: (id: string) => ({
      root: `/clients/${id}/overview`,
      accounts: `/clients/${id}/accounts`,
      performance: `/clients/${id}/performance`,
      details: `/clients/${id}/details`,
      transfers: {
        root: `/clients/${id}/transfers`,
        transfer: (transferId: string) => ({
          root: `/clients/${id}/transfers/${transferId}`,
          edit: (stepId?: string) => `/clients/${id}/transfers/${transferId}/edit${stepId ? `#${stepId}` : ''}`,
        }),
      },
      fees: {
        root: `/clients/${id}/fees`,
        edit: (stepId?: string) => `/clients/${id}/fees/edit${stepId ? `#${stepId}` : ''}`,
      },
      notifications: `/clients/${id}/notifications`,
      capitalGains: `/clients/${id}/capital-gains`,
      transactions: `/clients/${id}/transactions`,
      transactionsQuery: (params: Record<string, string>) =>
        `/clients/${id}/transactions?${new URLSearchParams(params).toString()}`,
      createChild: `/clients/${id}/createChild`,
      createAccount: `/clients/${id}/accounts/create`,
      edit: (stepId?: string) => `/clients/${id}/edit${stepId ? `#${stepId}` : ''}`,
      internalMovement: `/clients/${id}/internal-movement`,
      illustrations: `/clients/${id}/illustrations`,
      clientProductActions: `/clients/${id}/client-product-actions`,
    }),
  },
  accounts: {
    root: '/accounts',
    account: (accountId: string) => ({
      root: `/accounts/${accountId}/investments`,
      report: `/accounts/${accountId}/report`,
      transactions: `/accounts/${accountId}/transactions`,
      transactionsQuery: (params: Record<string, string>) =>
        `/accounts/${accountId}/enhanced-transactions?${new URLSearchParams(params).toString()}`,
      enhancedTransactions: `/accounts/${accountId}/enhanced-transactions`,
      transfers: { transfer: (transferId: string) => `/accounts/${accountId}/transfers/${transferId}` },
      details: `/accounts/${accountId}/details`,
      edit: (stepId?: string) => `/accounts/${accountId}/edit${stepId ? `#${stepId}` : ''}`,
      allTransactions: (assetId: string) => `/accounts/${accountId}/assets/${assetId}/all-transactions`,
      capitalGainsScenarios: `/accounts/${accountId}/capital-gains-scenarios`,
      actions: {
        withdraw: `/accounts/${accountId}/withdraw`,
        payIn: `/accounts/${accountId}/pay-in`,
        sell: `/accounts/${accountId}/sell`,
        crystallise: `/accounts/${accountId}/crystallise`,
        invest: `/accounts/${accountId}/invest`,
        adHocFee: `/accounts/${accountId}/ad-hoc-fee`,
        transfer: (transferId?: string) =>
          `/accounts/${accountId}/transfer${transferId ? `?transferId=${transferId}` : ''}`,
        rebalance: `/accounts/${accountId}/rebalance`,
      },
    }),
  },
  models: (modelType: 'advisory' | 'discretionary') => ({
    root: `/models/${modelType}`,
    create: `/models/${modelType}/create`,
    model: (modelId: string) => ({
      root: `/models/${modelType}/${modelId}`,
      edit: (stepId?: string) => `/models/${modelType}/${modelId}/edit${stepId ? `#${stepId}` : ''}`,
      clone: `/models/${modelType}/${modelId}/clone`,
      rebalance: `/models/discretionary/${modelId}/rebalance`,
    }),
  }),
  illustrations: {
    root: '/illustrations',
    create: (params?: {
      illustrationId?: string;
      clientId?: string;
      returnUrl?: string;
      skipPdfDownload?: boolean;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.illustrationId) searchParams.set('illustrationId', params.illustrationId);
      if (params?.clientId) searchParams.set('clientId', params.clientId);
      if (params?.returnUrl) searchParams.set('returnUrl', params.returnUrl);
      if (params?.skipPdfDownload) searchParams.set('skipPdfDownload', 'true');
      const query = searchParams.toString();
      return `/illustrations/create${query ? `?${query}` : ''}`;
    },
    illustration: (illustrationId: string) => ({
      root: `/illustrations/${illustrationId}`,
    }),
  },
  transactions: {
    root: '/transactions',
    query: (params: Record<string, string>) => `/transactions?${new URLSearchParams(params).toString()}`,
    transaction: (transactionId: string) => ({
      root: `/transactions/${transactionId}`,
    }),
  },
  instruments: {
    root: `/instruments/existing`,
    available: `/instruments/available`,
    instrument: (instrumentId: string) => ({
      root: `/instruments/${instrumentId}`,
      request: `/instruments/${instrumentId}/request`,
      edit: (stepId?: string) => `/instruments/${instrumentId}/edit${stepId ? `#${stepId}` : ''}`,
    }),
  },
  reporting: '/reporting',
  transfers: (params?: string | string[][] | Record<string, string> | URLSearchParams | undefined) =>
    `/transfers${params ? `?${new URLSearchParams(params).toString()}` : ''}`,
  migrations: {
    root: '/migrations',
    create: '/migrations/create',
  },
  users: {
    root: '/users',
    create: '/users/create',
    user: (id: string) => ({
      root: `/users/${id}`,
      edit: (stepId?: string) => `/users/${id}/edit${stepId ? `#${stepId}` : ''}`,
    }),
  },
  firmSettings: {
    professional: {
      theme: '/firm/professional/theme',
      content: '/firm/professional/content',
      configuration: '/firm/professional/configuration',
      wealthsweet: '/firm/professional/wealthsweet',
    },
    investor: {
      theme: '/firm/investor/theme',
      content: '/firm/investor/content',
      configuration: '/firm/investor/configuration',
    },
    media: {
      logos: '/firm/media/logos',
    },
    documents: {
      theme: '/firm/documents/theme',
      content: '/firm/documents/content',
    },
    email: {
      configuration: '/firm/email/configuration',
      content: '/firm/email/content',
    },
  },
  cookies: {
    root: '/cookies',
  },
};
